/**
 * Main API Client
 *
 * This file provides backwards compatibility by re-exporting all API modules
 * through a unified apiClient interface.
 *
 * For new code, prefer importing from specific modules:
 * - import { authApi } from './auth'
 * - import { adminApi } from './admin'
 * - import { portalApi } from './portal'
 * - import { serviceIntegratorApi } from './service-integrator'
 * - import { customerApi } from './customer'
 */

import { baseClient, http } from './base';
import { authApi } from './auth';
import { adminApi } from './admin';
import { portalApi } from './portal';
import { customerApi } from './customer';
import { chatApi } from './chat';
import { supportApi } from './support';
import type { User, GetAllUsersFilters } from '../types';

// Re-export all types from modules
export * from './auth';
export * from './portal';
export * from './customer';
export * from './chat';
export * from './support';

// Re-export base client for direct access
export { baseClient, http } from './base';

/**
 * Unified API Client
 *
 * Maintains backwards compatibility with the old monolithic client.
 * Delegates all calls to the appropriate module.
 */
class ApiClient {
  // ===== TOKEN MANAGEMENT (delegated to baseClient) =====

  setCustomerToken(token: string) {
    baseClient.setCustomerToken(token);
  }

  setServiceIntegratorToken(token: string) {
    baseClient.setServiceIntegratorToken(token);
  }

  setPortalToken(token: string) {
    baseClient.setPortalToken(token);
  }

  setDirectUserToken(token: string) {
    baseClient.setDirectUserToken(token);
  }

  clearAuth() {
    baseClient.clearAuth();
  }

  loadCustomerToken(): boolean {
    return baseClient.loadCustomerToken();
  }

  loadServiceIntegratorToken(): boolean {
    return baseClient.loadServiceIntegratorToken();
  }

  loadPortalToken(): boolean {
    return baseClient.loadPortalToken();
  }

  loadDirectUserToken(): boolean {
    return baseClient.loadDirectUserToken();
  }

  getPortalToken(): string | undefined {
    return baseClient.getPortalToken();
  }

  getDirectUserToken(): string | undefined {
    return baseClient.getDirectUserToken();
  }

  // ===== AUTH (delegated to authApi) =====

  portalRegister = authApi.portalRegister;
  portalVerifyEmail = authApi.portalVerifyEmail;
  portalResendVerification = authApi.portalResendVerification;
  portalLogin = authApi.portalLogin;
  portalMagicLinkRequest = authApi.portalMagicLinkRequest;
  portalMagicLinkVerify = authApi.portalMagicLinkVerify;
  portalForgotPassword = authApi.portalForgotPassword;
  portalResetPassword = authApi.portalResetPassword;
  portalOAuthGoogle = authApi.portalOAuthGoogle;
  portalOAuthGithub = authApi.portalOAuthGithub;
  portalGetSession = authApi.portalGetSession;
  getOrganizations = authApi.getOrganizations;
  switchOrganization = authApi.switchOrganization;
  portalOAuthCallback = authApi.portalOAuthCallback;
  portalLogout = authApi.portalLogout;
  adminLogin = authApi.adminLogin;
  adminLogout = authApi.adminLogout;
  getAdminSession = authApi.getAdminSession;
  // Customer/SI/direct-user login methods retired (2026-04-27).
  // The flows themselves no longer exist; admin elevation handles
  // enterprise customers via the standard portal account.
  ssoCheckConfig = authApi.ssoCheckConfig;
  getSsoLoginUrl = authApi.getSsoLoginUrl;
  ssoValidateSession = authApi.ssoValidateSession;
  ssoLogout = authApi.ssoLogout;
  getSsoConfig = authApi.getSsoConfig;
  saveSsoConfig = authApi.saveSsoConfig;
  deleteSsoConfig = authApi.deleteSsoConfig;
  getSpMetadata = authApi.getSpMetadata;
  getSsoSessions = authApi.getSsoSessions;
  getSsoStats = authApi.getSsoStats;
  parseIdpMetadata = authApi.parseIdpMetadata;

  // ===== PORTAL (delegated to portalApi) =====

  portalUpdateProfile = portalApi.updateProfile;
  portalChangePassword = portalApi.changePassword;
  portalDeleteAccount = portalApi.deleteAccount;
  portalGetPricing = portalApi.getPricing;
  portalCheckout = portalApi.checkout;
  portalGetPaymentMethods = portalApi.getPaymentMethods;
  portalCreateLightningInvoice = portalApi.createLightningInvoice;
  portalVerifyLightningPayment = portalApi.verifyLightningPayment;
  portalGetLightningStatus = portalApi.getLightningStatus;
  portalGetSubscription = portalApi.getSubscription;
  portalUpdateSubscription = portalApi.updateSubscription;
  portalCancelSubscription = portalApi.cancelSubscription;
  portalGetInvoices = portalApi.getInvoices;
  portalGetModules = portalApi.getModules;
  portalAddModule = portalApi.addModule;
  portalRemoveModule = portalApi.removeModule;
  portalGetTeamMembers = portalApi.getTeamMembers;
  portalInviteTeamMember = portalApi.inviteTeamMember;
  portalAcceptInvite = portalApi.acceptInvite;
  portalResendInvite = portalApi.resendInvite;
  portalUpdateMemberRole = portalApi.updateMemberRole;
  portalRemoveMember = portalApi.removeMember;
  portalLeaveTeam = portalApi.leaveTeam;
  portalSetupOrganization = portalApi.setupOrganization;
  portalUpdateOrganization = portalApi.updateOrganization;

  // ===== ADMIN (delegated to adminApi) =====
  // Customer / Service-Integrator / SI-themes / legacy enterprise-user
  // re-exports were all removed alongside the deleted backend endpoints
  // and dead UI sections — see git log for the audit trail.

  // getDashboardStats went with GET /api/admin/stats/dashboard; the cockpit
  // reads adminApi.getCockpit() directly.
  getPortalUsers = adminApi.getPortalUsers;
  updatePortalUser = adminApi.updatePortalUser;
  updatePortalUserTrial = adminApi.updatePortalUserTrial;
  updatePortalUserSubscription = adminApi.updatePortalUserSubscription;
  getPortalUserModules = adminApi.getPortalUserModules;
  setPortalUserModules = adminApi.setPortalUserModules;
  deletePortalUser = adminApi.deletePortalUser;

  // Direct-request delegation removed alongside the adminApi methods —
  // the backend never mounted /api/direct-requests after the
  // org-unification migration, so every call 404'd.

  getAnonymousTelemetryStats = adminApi.getAnonymousTelemetryStats;
  getErrorTelemetrySessions = adminApi.getErrorTelemetrySessions;
  getAiCrawlerStats = adminApi.getAiCrawlerStats;
  runAiCrawlerSync = adminApi.runAiCrawlerSync;
  sendTrialMagicLink = adminApi.sendTrialMagicLink;

  // BILLING delegation removed entirely along with api/billing.ts (wave 5).
  // The module held 37 methods, 26 of them unique urls under
  // `/api/admin/direct-billing/*` — a prefix the backend has never mounted.
  // The April branding sweep renamed them there from
  // `/api/admin/enterprise-billing/*` and every call has 404'd since; the
  // pages that made them (BillingDashboard, Invoices, InvoiceDetail) never
  // destructured `error`, so a dead endpoint and an empty administration
  // looked identical. The remaining eleven pointed at `/api/customer/*` and
  // `/api/service-integrator/*` invoice routes that read `customers` and
  // `service_integrators`, both dropped by migration 114, and no frontend
  // file called them.
  // What replaced the need: GET /api/admin/revenue reads the real `invoices`
  // and `payments` tables, which until now had no admin read path at all.
  // A customer downloads their own PDF straight from
  // /api/portal/billing/invoices/:id/pdf (pages/customer/Billing.tsx has its
  // own fetch), so nothing on the customer side went through here.

  // SERVICE INTEGRATOR delegation removed entirely along with
  // api/service-integrator.ts — SIs are now just orgs with
  // plan_type='enterprise' and live under /admin/orgs.

  // ===== CUSTOMER (delegated to customerApi) =====

  getProfile = customerApi.getProfile;
  getUsageStats = customerApi.getUsageStats;
  getUsageTimeline = customerApi.getUsageTimeline;
  getToolUsage = customerApi.getToolUsage;
  getCustomerStats = customerApi.getStats;
  getCustomerUsers = customerApi.getUsers;
  getAllCustomerUsers = customerApi.getAllUsers;
  createCustomerUser = customerApi.createUser;
  updateCustomerUser = customerApi.updateUser;
  deleteCustomerUser = customerApi.deleteUser;
  updateCustomerSeats = customerApi.updateSeats;
  getCredentials = customerApi.getCredentials;
  addCredential = customerApi.addCredential;
  updateCredential = customerApi.updateCredential;
  deleteCredential = customerApi.deleteCredential;
  testCredential = customerApi.testCredential;
  updateCredentialScoping = customerApi.updateCredentialScoping;
  getCredentialAccess = customerApi.getCredentialAccess;
  grantCredentialAccess = customerApi.grantCredentialAccess;
  revokeCredentialAccess = customerApi.revokeCredentialAccess;
  getUserCredentials = customerApi.getUserCredentials;
  getAccessibleOrgCredentials = customerApi.getAccessibleOrgCredentials;
  testOrgCredential = customerApi.testOrgCredential;
  getUserCredential = customerApi.getUserCredential;
  addUserCredential = customerApi.addUserCredential;
  updateUserCredential = customerApi.updateUserCredential;
  deleteUserCredential = customerApi.deleteUserCredential;
  testUserCredential = customerApi.testUserCredential;
  getServiceNowInstances = customerApi.getInstances;
  getServiceNowInstance = customerApi.getInstance;
  createServiceNowInstance = customerApi.createInstance;
  updateServiceNowInstance = customerApi.updateInstance;
  deleteServiceNowInstance = customerApi.deleteInstance;
  testServiceNowInstance = customerApi.testInstance;
  setDefaultServiceNowInstance = customerApi.setDefaultInstance;
  updateServiceNowInstanceScoping = customerApi.updateInstanceScoping;
  getServiceNowInstanceAccess = customerApi.getInstanceAccess;
  grantServiceNowInstanceAccess = customerApi.grantInstanceAccess;
  revokeServiceNowInstanceAccess = customerApi.revokeInstanceAccess;
  getAccessibleServiceNowInstances = customerApi.getAccessibleInstances;
  getDefaultServiceNowInstance = customerApi.getDefaultInstance;
  getServiceNowUpdateSets = customerApi.getUpdateSets;
  getServiceNowUpdateSetUpdates = customerApi.getUpdateSetUpdates;
  getAgentActivities = customerApi.getActivities;
  getAgentActivityStats = customerApi.getActivityStats;
  getAgentActivity = customerApi.getActivity;
  deleteAgentActivity = customerApi.deleteActivity;
  exportAgentActivities = customerApi.exportActivities;
  getActivityTrends = customerApi.getActivityTrends;
  getNotificationSettings = customerApi.getNotificationSettings;
  updateNotificationSettings = customerApi.updateNotificationSettings;
  testNotification = customerApi.testNotification;
  getInAppNotifications = customerApi.getInAppNotifications;
  getUnreadNotificationCount = customerApi.getUnreadNotificationCount;
  markNotificationAsRead = customerApi.markNotificationAsRead;
  markAllNotificationsAsRead = customerApi.markAllNotificationsAsRead;
  deleteInAppNotification = customerApi.deleteNotification;
  getNotificationHistory = customerApi.getNotificationHistory;
  getHealth = customerApi.getHealth;
  getMetrics = customerApi.getMetrics;
  getDeletionFeedback = customerApi.getDeletionFeedback;

  // ===== WIDGET PREVIEW =====
  searchPreviewWidgets = customerApi.searchPreviewWidgets;
  searchPreviewArtifacts = customerApi.searchPreviewArtifacts;
  getPreviewWidget = customerApi.getPreviewWidget;
  getPreviewArtifact = customerApi.getPreviewArtifact;
  getFlowStructure = customerApi.getFlowStructure;
  getPageLayout = customerApi.getPageLayout;
  parseDataShape = customerApi.parseDataShape;

  // The four /api/upgrade-requests/service-integrator/* methods and the three
  // /api/admin/si-credentials/* methods were removed with the rest of the SI
  // surface — no caller was left after SIs became ordinary enterprise orgs,
  // and the si-credentials prefix has never existed in the backend. Their one
  // consumer, components/admin/IntegrationsSection.tsx, went with them.
  // Admin review lives below.

  // The three ADMIN UPGRADE REQUEST methods stood here until wave 5:
  // getAdminUpgradeRequests, reviewAdminUpgradeRequest, getUpgradeRequestStats.
  // They hit the same three URLs as adminApi.getRevenueUpgradeRequests /
  // reviewRevenueUpgradeRequest / getRevenueUpgradeRequestStats, with a second
  // set of types, and their only caller was the deleted /admin/upgrade-requests
  // page. Two client layers over one backend route is how the next backend
  // change ends up half-applied.

}

// Singleton instance
export const apiClient = new ApiClient();

export default apiClient;
